import React, { useState } from 'react';
import { EmailConnection, EmailProvider, useEmailConnectionsService } from 'nekst-api';
import { Button, ButtonWithImage, ButtonColor, ButtonSize, ButtonVariant, Text } from 'ui-builder';
import { CardLayout, ErrorMessage, IconDeleteButton } from 'features/nekst-widgets';

import styles from './EmailWidget.module.scss';

export interface ProviderConfig {
  name: string;
  icon: string;
  provider: EmailProvider;
  lockedByProvider: string;
}

export interface ProviderCardProps {
  connection: EmailConnection | null;
  isLocked: boolean;
  onConnectionChange: () => void;
}

function getProviderIcon(config: ProviderConfig) {
  return <img src={config.icon} alt={config.name} width="24" height="24" />;
}

function formatConnectionDate(dateString: string) {
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return dateString;
  }
}

function ReauthenticateButton(props: {
  config: ProviderConfig;
  onConnectionChange: () => void;
}) {
  const emailConnectionsService = useEmailConnectionsService();

  const handleReauthenticate = async () => {
    await emailConnectionsService.deleteConnection();
    props.onConnectionChange();

    const oauthResponse = await emailConnectionsService.getOAuthUrl(props.config.provider);
    window.location.href = oauthResponse.url;
  };

  return (
    <Button
      text="Re-authenticate"
      size={ButtonSize.SMALL}
      variant={ButtonVariant.FILLED}
      color={ButtonColor.YELLOW}
      onClick={handleReauthenticate}
    />
  );
}

function ConnectedCard(props: {
  config: ProviderConfig;
  connection: EmailConnection;
  onDisconnect: () => Promise<void>;
  onConnectionChange: () => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const handleDisconnect = async () => {
    try {
      await props.onDisconnect();
    } catch (e) {
      console.error('Failed to disconnect:', e);
      setError('Failed to disconnect. Please try again.');
    }
  };

  return (
    <>
      <CardLayout
        icon={getProviderIcon(props.config)}
        title={
          <div className={styles.titleWithStatus}>
            <span>{props.config.name} Connected</span>
            {!props.connection.isValid && (
              <span className={styles.statusBadge}>
                <span role="img" aria-label="warning">⚠️</span> Needs Attention
              </span>
            )}
          </div>
        }
        subtitle={
          <div>
            <Text>{props.connection.email}</Text>

            {props.connection.isValid && (
              <Text>
                {' • '}
                <Text>Connected on: {formatConnectionDate(props.connection.createdAt)}</Text>
              </Text>
            )}

            {!props.connection.isValid && (
              <div className={styles.warningSection}>
                <div className={styles.warningMessage}>
                  <span className={styles.warningIcon} role="img" aria-label="warning">⚠️</span>
                  <Text className={styles.warningText}>
                    Authentication expired. You need to re-authenticate to continue sending emails.
                  </Text>
                </div>
              </div>
            )}
          </div>
        }
        actions={(
          <>
            {props.connection.isValid && (
              <IconDeleteButton
                message={`Are you sure you want to disconnect your ${props.config.name} account? You will need to reconnect to continue sending emails through ${props.config.name}.`}
                deleteFunc={handleDisconnect}
              />
            )}
            {!props.connection.isValid && (
              <ReauthenticateButton
                config={props.config}
                onConnectionChange={props.onConnectionChange}
              />
            )}
          </>
        )}
      />
      {error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}
    </>
  );
}

function AvailableCard(props: {
  config: ProviderConfig;
  onConnect: () => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);

  const handleConnect = async () => {
    try {
      setError(null);
      await props.onConnect();
    } catch (e) {
      setError('Failed to start connection. Please try again.');
    }
  };

  return (
    <>
      <CardLayout
        icon={getProviderIcon(props.config)}
        title={props.config.name}
        subtitle={`Connect your ${props.config.name} account to send emails directly through your personal email address`}
        actions={
          <ButtonWithImage
            image={getProviderIcon(props.config)}
            text="Connect"
            size={ButtonSize.MEDIUM}
            color={ButtonColor.GREEN}
            onClick={handleConnect}
          />
        }
      />
      {error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}
    </>
  );
}

function LockedCard(props: { config: ProviderConfig }) {
  return (
    <div className={styles.lockedCard}>
      <CardLayout
        icon={getProviderIcon(props.config)}
        title={props.config.name}
        subtitle={
          <Text className={styles.lockedMessage}>
            {props.config.lockedByProvider} is currently connected. To use {props.config.name}, disconnect {props.config.lockedByProvider} first.
          </Text>
        }
      />
    </div>
  );
}

export default function BaseProviderCard(props: ProviderCardProps & { config: ProviderConfig }) {
  const emailConnectionsService = useEmailConnectionsService();

  const handleConnect = async () => {
    const oauthResponse = await emailConnectionsService.getOAuthUrl(props.config.provider);
    window.location.href = oauthResponse.url;
  };

  const handleDisconnect = async () => {
    await emailConnectionsService.deleteConnection();
    props.onConnectionChange();
  };

  if (props.isLocked) {
    return <LockedCard config={props.config} />;
  } else if (props.connection) {
    return (
      <ConnectedCard
        config={props.config}
        connection={props.connection}
        onDisconnect={handleDisconnect}
        onConnectionChange={props.onConnectionChange}
      />
    );
  } else {
    return <AvailableCard config={props.config} onConnect={handleConnect} />;
  }
}
